import { useEffect, useState, type FormEvent } from "react";
import { loadGuardianLogin, remoteClient } from "../data/remote";
import {
  describeGuardianLogin,
  guardianLoginError,
  guardianLoginSummary,
  resolveLocalLogin,
  type GuardianLoginSummary,
  type ResolvedLogin,
} from "../domain/guardianLogin";
import { useAuth } from "../state/AuthProvider";
import { useStudents } from "../state/students";

type RemoteState =
  | { email: string; phase: "loading" }
  | { email: string; phase: "error"; message: string }
  | { email: string; phase: "ready"; login: ResolvedLogin | null };

type Readout =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; summary: GuardianLoginSummary };

function useGuardianLoginReadout(email: string, enabled: boolean): Readout {
  const { accounts } = useAuth();
  const { students } = useStudents();
  const [remote, setRemote] = useState<RemoteState | null>(null);
  const hosted = enabled && remoteClient() !== null;

  useEffect(() => {
    if (!enabled) return;
    const client = remoteClient();
    if (!client) return;
    let active = true;
    setRemote({ email, phase: "loading" });
    void loadGuardianLogin(client, email)
      .then((login) => {
        if (active) setRemote({ email, phase: "ready", login });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : "";
        setRemote({ email, phase: "error", message: guardianLoginError(message) });
      });
    return () => {
      active = false;
    };
  }, [email, enabled]);

  if (!hosted) {
    return {
      phase: "ready",
      summary: guardianLoginSummary(
        describeGuardianLogin(resolveLocalLogin(accounts, email), students, email),
      ),
    };
  }
  if (!remote || remote.email !== email || remote.phase === "loading") {
    return { phase: "loading" };
  }
  if (remote.phase === "error") return { phase: "error", message: remote.message };
  return {
    phase: "ready",
    summary: guardianLoginSummary(
      describeGuardianLogin(remote.login, students, email),
    ),
  };
}

function LoginStatusView({ readout, heading }: { readout: Readout; heading: boolean }) {
  const title =
    readout.phase === "loading"
      ? "Checking this email"
      : readout.phase === "error"
        ? "Lookup failed"
        : readout.summary.title;
  const detail =
    readout.phase === "loading"
      ? "Looking up whether this guardian email has a login."
      : readout.phase === "error"
        ? readout.message
        : readout.summary.detail;
  return (
    <div className="login-status" role="status">
      {heading ? <div className="meta">Family login</div> : null}
      <strong>{title}</strong>
      <p className="anno">{detail}</p>
    </div>
  );
}

/** Staff student profile. Hidden for every other role. */
export function GuardianLoginStatus({
  email,
  heading = true,
}: {
  email: string;
  heading?: boolean;
}) {
  const { account } = useAuth();
  const enabled = account?.role === "staff";
  const readout = useGuardianLoginReadout(email, enabled);
  if (!enabled) return null;
  return <LoginStatusView readout={readout} heading={heading} />;
}

/** Staff roster search. One email at a time, with no roster column. */
export function GuardianLoginSearch() {
  const { account } = useAuth();
  const [draft, setDraft] = useState("");
  const [email, setEmail] = useState("");
  if (account?.role !== "staff") return null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setEmail(draft.trim());
  }

  return (
    <section className="wire login-lookup">
      <div className="meta">Family login</div>
      <p className="anno">
        When a family calls because they do not remember an account, look up the
        guardian email.
      </p>
      <form className="login-lookup__row" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="guardian-login-email">Guardian email</label>
          <input
            id="guardian-login-email"
            type="email"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            autoComplete="off"
            required
          />
        </div>
        <div className="actions">
          <button type="submit">Look up</button>
        </div>
      </form>
      {email ? <GuardianLoginStatus email={email} heading={false} /> : null}
    </section>
  );
}
